// ============================================
// CREATE RECOMMEND HOOKS
// Combines hooks from:
// - [BuilderWhoOptions.tsx] -> CreateBuilder (WHO 001-019)
// - [BuilderWhatOptions.tsx] -> CreateBuilder (WHAT 020-039)
// - [BuilderHowOptions.tsx] -> CreateBuilder (HOW 040-059)
// - [BuilderSPPOptions.tsx] -> CreateBuilder (HOW 040-059)
// - [BuilderWhenOptions.tsx] -> CreateBuilder (WHEN 060-079)
// - [BuilderPlanOptions.tsx] -> CreateBuilder (PLAN 080-099)
// ============================================

import React, {useEffect, useMemo} from 'react';
import {useAccountContext} from '../../../contexts/AccountContext';
import OWNERSHIP from '../../../constants/Ownership';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {ReviewDetailsFragment} from '../../../schema-types/ReviewDetailsFragment';
import {Option} from '../../../components/AutoForm/AutoFieldNew';
import {
  useAutoForm,
  useAutoField,
  AutoFieldHelpers,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import {
  AccountBasicFragment,
  AccountBasicFragment_contacts,
} from '../../../schema-types/AccountBasicFragment';
import {UPDATES} from '../../../shared/UPDATES';
import {URGENCY} from '../../../constants/Urgency';

// ============================================
// WHO (001-019) - from BuilderWhoOptions.tsx
// ============================================

export const loadWhoBuilds = ({
  builderDetails,
  reviewDetails,
  setFieldValue,
  account,
}: {
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  reviewDetails?: ReviewDetailsFragment;
  account: AccountBasicFragment | undefined;
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
}) => {
  // if (!reviewDetails) {
  //   return;
  // }
  // if (builderDetails?.contact?.id) {
  //   setFieldValue(UPDATES._001, 'contact', builderDetails.contact.id);
  // } else if (reviewDetails?.contact?.id) {
  //   setFieldValue(UPDATES._002, 'contact', reviewDetails.contact.id);
  // } else if (account?.isTrust) {
  //   setFieldValue(UPDATES._003, 'contact', OWNERSHIP.TRUST);
  // } else {
  //   setFieldValue(UPDATES._004, 'contact', OWNERSHIP.JOINT_CODE);
  // }
  // // setFieldValue(UPDATES._005, 'ownership', builderDetails?.ownership);
  // setFieldValue(UPDATES._006, 'goal', builderDetails?.goal);
};

export const setBuilderContact = ({
  contacts,
  setFieldValue,
  setLabel,
  reviewDetails,
  builderDetails,
  values,
  account,
}: {
  setLabel: React.Dispatch<React.SetStateAction<string>>;
  setFieldValue: SetFieldValueWithSourceKey<builderFieldsType>;
  contacts: AccountBasicFragment_contacts[];
  reviewDetails: ReviewDetailsFragment | undefined;
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  values: builderFieldsType;
  account: AccountBasicFragment | undefined;
}) => {
  if (!reviewDetails) {
    return;
  }

  if (account?.isTrust) {
    // setFieldValue('ownership', OWNERSHIP.WHOLE_FAMILY, UPDATES._007);
    setLabel('for an account');

    setFieldValue(UPDATES._008, 'contact', OWNERSHIP.TRUST);
  } else {
    if (!values.contact) {
      const defaultContact = contacts && contacts.length ? contacts[0].id : null;

      setFieldValue(
        UPDATES._009,
        'contact',
        builderDetails?.contact?.id ||
          reviewDetails?.contact?.id ||
          (contacts.length > 1 ? OWNERSHIP.JOINT_CODE : defaultContact)
      );
    }
    if (values.contact === OWNERSHIP.JOINT_CODE) {
      // setFieldValue('ownership', OWNERSHIP.JOINT_LIVES, UPDATES._010);
      setLabel('for a joint account');
    } else if (values.contact) {
      const contact = contacts.find((c) => c.id === values.contact);

      // If the contact is not found that means we have a value
      // that's no longer present or allowed
      if (contact) {
        setLabel(`for ${contact.simplifiedName}`);
        // setFieldValue('ownership', OWNERSHIP.SINGLE_LIVES, UPDATES._011);
      } else {
        setLabel('for a contact');
      }
    }
  }
  if (builderDetails?.goal) {
    setFieldValue(UPDATES._006, 'goal', builderDetails?.goal);
  }
};

// ============================================
// WHAT (020-039) - from BuilderWhatOptions.tsx
// ============================================

// ============================================
// WHEN (060-079) - from BuilderWhenOptions.tsx
// ============================================

export const loadUrgencyDefaults = ({
  urgencyValue,
  urgencyHelpers,
  urgencyReasonHelpers,
}: {
  urgencyValue: builderFieldsType['urgency'];
  urgencyHelpers: AutoFieldHelpers<builderFieldsType['urgency']>;
  urgencyReasonHelpers: AutoFieldHelpers<builderFieldsType['urgencyReason']>;
}) => {
  if (!urgencyValue) {
    urgencyHelpers.setValue(UPDATES._062, URGENCY.NORMAL_ID);
  }
  if (urgencyValue === URGENCY.TAX_ID) {
    urgencyReasonHelpers.setValue(UPDATES._063, 'Tax year end');
  } else {
    urgencyReasonHelpers.setValue(UPDATES._064, '');
  }
};

export const useLoadBuilderUrgencyDefaults = () => {
  const [urgencyInput, , urgencyHelpers] =
    useAutoField<builderFieldsType['urgency']>('urgency');
  const [, , urgencyReasonHelpers] =
    useAutoField<builderFieldsType['urgencyReason']>('urgencyReason');
  const {value: urgencyValue} = urgencyInput;
  useEffect(() => {
    loadUrgencyDefaults({urgencyValue, urgencyHelpers, urgencyReasonHelpers});
  }, [urgencyValue]);
};

// ============================================
// COMPUTED FIELDS (not stored in state)
// ============================================

export const useIsMinor = (): boolean => {
  const {values} = useAutoForm<builderFieldsType>();
  const {account} = useAccountContext();
  const contacts = account?.contacts || [];
  return useMemo(() => {
    if (!values.contact) {
      return false;
    }
    const contact = contacts.find((c) => c.id === values.contact);

    return Boolean(contact?.isMinor);
  }, [contacts, values.contact]);
};

export const useBuilderAdviceWhat = (
  planActionsDynamic: Record<
    string,
    {optionLabel: string; label: string; adviceLabel: string}
  >
): string => {
  const {values} = useAutoForm<builderFieldsType>();
  return useMemo(() => {
    if (!values.planAction) {
      return '';
    }
    return planActionsDynamic[values.planAction]?.adviceLabel || '';
  }, [values.planAction, planActionsDynamic]);
};

export const useBuilderAdvicePlan = (planTypes: Option<string>[]): string => {
  const {values} = useAutoForm<builderFieldsType>();
  return useMemo(() => {
    if (!values.mainPlanType) {
      return '';
    }
    const plan = planTypes.find((p) => p.value === values.mainPlanType);
    if (!plan) {
      return '';
    }
    return plan.capitalise ? plan.label : plan.label.toLowerCase();
  }, [values.mainPlanType, planTypes]);
};
